import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Paper, Typography, TextField, MenuItem, Button, Chip, FormControlLabel, Switch, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, LinearProgress, CircularProgress,
} from '@mui/material';
import { FileDownload as DownloadIcon, Print as PrintIcon } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { accountingAPI, academicAPI } from '../../services/api';
import { money, errorText } from '../../utils/format';
import { fmtDate } from '../../utils/dates';
import { printPage, esc } from '../../utils/print';
import { MiniStat } from './common';

const STATUS = {
  PAID: { label: 'Paid', bg: '#c8e6c9', fg: '#1b5e20' },
  PARTIAL: { label: 'Part paid', bg: '#ffe0b2', fg: '#e65100' },
  UNPAID: { label: 'Not paid', bg: '#ffcdd2', fg: '#b71c1c' },
  NO_FEE: { label: 'No fee set', bg: '#eceff1', fg: '#546e7a' },
};

export default function FeeDuesTab({ notify }) {
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [onlyDue, setOnlyDue] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  useEffect(() => { academicAPI.getClasses().then((r) => setClasses(r.data)).catch(() => setClasses([])); }, []);
  useEffect(() => {
    setSectionId('');
    if (!classId) { setSections([]); return; }
    academicAPI.getSectionsByClass(classId).then((r) => setSections(r.data)).catch(() => setSections([]));
  }, [classId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await accountingAPI.getFeeDues({ classId: classId || undefined, sectionId: sectionId || undefined, onlyDue });
        if (!cancelled) { setData(r.data); setPage(0); }
      } catch (e) { notify(errorText(e, 'Could not load fee dues'), 'error'); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [classId, sectionId, onlyDue, notify]);

  const rows = data?.rows || [];
  const shown = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const scopeLabel = classes.find((c) => String(c.id) === String(classId))?.name || 'All programs';

  const exportExcel = () => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows.map((r) => ({
      'Admission no': r.admissionNo, Student: r.name, Program: r.className, Batch: r.sectionName, Guardian: r.guardianName || '', Phone: r.guardianPhone || '',
      'Total fee (₹)': Number(r.total), 'Paid (₹)': Number(r.paid), 'Due (₹)': Number(r.due), 'Last payment': r.lastPaymentDate || '', Status: STATUS[r.status]?.label,
    }))), 'Fee dues');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet((data?.byClass || []).map((c) => ({
      Program: c.className, Students: c.students, 'Total fee (₹)': Number(c.totalFee), 'Collected (₹)': Number(c.paid), 'Outstanding (₹)': Number(c.due),
    }))), 'By program');
    XLSX.writeFile(wb, 'fee_dues.xlsx');
  };

  const print = () => {
    const body = `<div class="grid">
        <div class="card"><div class="v">${data.students}</div><div class="l">Students listed</div></div>
        <div class="card"><div class="v">${money(data.totalFee)}</div><div class="l">Total fee</div></div>
        <div class="card"><div class="v green">${money(data.totalPaid)}</div><div class="l">Collected</div></div>
        <div class="card"><div class="v red">${money(data.totalDue)}</div><div class="l">Outstanding</div></div></div>
      <table><thead><tr><th>Adm. no</th><th>Student</th><th>Program / batch</th><th>Guardian</th><th>Phone</th><th class="r">Total</th><th class="r">Paid</th><th class="r">Due</th><th>Last paid</th></tr></thead><tbody>
      ${rows.map((r) => `<tr><td>${esc(r.admissionNo)}</td><td>${esc(r.name)}</td><td>${esc(r.className)} - ${esc(r.sectionName)}</td><td>${esc(r.guardianName)}</td><td>${esc(r.guardianPhone)}</td><td class="r">${money(r.total)}</td><td class="r">${money(r.paid)}</td><td class="r b ${Number(r.due) ? 'red' : ''}">${money(r.due)}</td><td>${esc(fmtDate(r.lastPaymentDate))}</td></tr>`).join('')}
      </tbody></table>`;
    printPage('Fee Dues Statement', `${scopeLabel}${onlyDue ? ' - students with dues' : ''} - ${data.year || ''}`, body);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField select size="small" label="Program / year" value={classId} onChange={(e) => setClassId(e.target.value)} sx={{ minWidth: 220 }}>
            <MenuItem value="">All programs</MenuItem>
            {classes.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Batch" value={sectionId} onChange={(e) => setSectionId(e.target.value)} sx={{ minWidth: 140 }} disabled={!classId}>
            <MenuItem value="">All batches</MenuItem>
            {sections.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
          </TextField>
          <FormControlLabel control={<Switch checked={onlyDue} onChange={(e) => setOnlyDue(e.target.checked)} />} label="Only students with dues" />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={exportExcel} disabled={!data}>Export Excel</Button>
          <Button variant="outlined" size="small" startIcon={<PrintIcon />} onClick={print} disabled={!rows.length}>Print</Button>
        </Box>
      </Box>

      {data && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}><MiniStat label={`Students with dues${onlyDue ? '' : ' / listed'}`} value={`${data.defaulters} / ${data.students}`} color="#c62828" bg="#ffebee" /></Grid>
            <Grid item xs={6} md={3}><MiniStat label="Total fee" value={money(data.totalFee)} /></Grid>
            <Grid item xs={6} md={3}><MiniStat label="Collected" value={money(data.totalPaid)} color="#00796b" bg="#e8f5e9" /></Grid>
            <Grid item xs={6} md={3}><MiniStat label="Outstanding" value={money(data.totalDue)} color="#e65100" bg="#fff3e0" /></Grid>
          </Grid>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Collection by program-year (fee year {data.year || '-'}, {Number(data.collectionPct).toFixed(1)}% collected overall)</Typography>
            {(data.byClass || []).map((c) => {
              const p = Number(c.totalFee) > 0 ? (Number(c.paid) / Number(c.totalFee)) * 100 : 0;
              return (
                <Box key={c.className} sx={{ mb: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                    <Typography variant="body2" fontWeight={600}>{c.className} <Typography component="span" variant="caption" color="text.secondary">({c.students} students)</Typography></Typography>
                    <Typography variant="body2"><b style={{ color: '#00796b' }}>{money(c.paid)}</b> of {money(c.totalFee)} &middot; due <b style={{ color: '#c62828' }}>{money(c.due)}</b></Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={Math.min(100, p)} sx={{ height: 8, borderRadius: 4, bgcolor: '#ffebee', '& .MuiLinearProgress-bar': { bgcolor: '#26a69a' } }} />
                </Box>
              );
            })}
            {(!data.byClass || data.byClass.length === 0) && <Typography variant="body2" color="text.secondary">No students found.</Typography>}
          </Paper>
        </>
      )}

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box> : (
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Admission no</TableCell><TableCell>Student</TableCell><TableCell>Program / batch</TableCell><TableCell>Guardian</TableCell>
                <TableCell align="right">Total fee</TableCell><TableCell align="right">Paid</TableCell><TableCell align="right">Due</TableCell><TableCell>Last paid</TableCell><TableCell align="center">Status</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {shown.map((r) => {
                  const st = STATUS[r.status] || STATUS.NO_FEE;
                  return (
                    <TableRow key={r.studentId} hover>
                      <TableCell>{r.admissionNo}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                      <TableCell>{r.className} - {r.sectionName}</TableCell>
                      <TableCell>{r.guardianName || '-'}<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{r.guardianPhone || ''}</Typography></TableCell>
                      <TableCell align="right">{money(r.total)}</TableCell>
                      <TableCell align="right" sx={{ color: '#00796b' }}>{money(r.paid)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: Number(r.due) ? '#c62828' : 'inherit' }}>{money(r.due)}</TableCell>
                      <TableCell>{fmtDate(r.lastPaymentDate)}</TableCell>
                      <TableCell align="center"><Chip size="small" label={st.label} sx={{ bgcolor: st.bg, color: st.fg, fontWeight: 700 }} /></TableCell>
                    </TableRow>
                  );
                })}
                {shown.length === 0 && <TableRow><TableCell colSpan={9} align="center" sx={{ py: 5, color: 'text.secondary' }}>{onlyDue ? 'No students have pending fees here.' : 'No students found.'}</TableCell></TableRow>}
              </TableBody>
            </Table>
            <TablePagination component="div" count={rows.length} page={page} onPageChange={(e, p) => setPage(p)} rowsPerPage={rowsPerPage} rowsPerPageOptions={[15, 30, 60]}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
